import React, { useEffect, useState } from 'react';
import { createFormBag, FormData, FormBag, FormErrors, validateFormBag, FormUpdateEvent } from 'react-formage'
import { Input } from 'components/ui/input'

import { Button, ButtonGroup } from 'components/ui/button'
import { Modal } from 'components/ui/modal'
import { StatusGuard } from 'components/ui/status-guard'

import { sharedActionCreators, SharedActionCreators } from 'containers/shared';
import { connect } from 'containers/store';

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions';
import { GettyStore } from 'domains/org/getty/store';

import { VisitorType } from 'generated/mos/org/getty/audio'
import { CreateVisitorTypeRequest } from 'generated/mos/org/getty/audiomanagement'

import { pick } from 'helpers/core'
import { Status } from 'helpers/status'

import { visitorTypeSchema } from '../../../schemas/visitor-type'

export type FormValues = {
  label: string;
  tag: string;
};

export const validate = (values: FormValues) => {
  const errors: FormErrors<FormValues> = {};
  if (!values.label.trim()) {
    errors.label = 'Label required';
  }
  if (!values.tag.trim()) {
    errors.tag = 'Tag required';
  }
  return errors;
};

type ConnectedProps = {
  visitorTypeCreate: GettyStore['getty']['visitorTypeCreate'];
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyCreateVisitorTypeRequest'> &
  Pick<SharedActionCreators, 'toastNotification'>;

type DirectProps = {
  onCancel: () => void;
  onComplete: (visitorType: VisitorType.Entity) => void;
};

type Props = ConnectedProps & ActionProps & DirectProps;

const VisitorTypeCreateView = (props: Props) => {
  const {
    gettyCreateVisitorTypeRequest,
    onComplete,
    visitorTypeCreate,
    toastNotification,
  } = props

  const createVisitorType = () => {
    const validatedBag = validateFormBag(bag, validate)
    setFormBag(validatedBag)

    if (visitorTypeCreate.status === Status.Idle && validatedBag.valid) {
      gettyCreateVisitorTypeRequest({
        ...CreateVisitorTypeRequest.defaults,
        label: visitorTypeSchema.label ? visitorTypeSchema.label.write(bag.values.label) : bag.values.label,
        tag: visitorTypeSchema.tag ? visitorTypeSchema.tag.write(bag.values.tag) : bag.values.tag,
        type: CreateVisitorTypeRequest.refName,
      })
    }

    if (visitorTypeCreate.status === Status.Failed) {
      toastNotification({
        type: 'error',
        text: 'Could not create Visitor Type',
      });
    }
  }

  useEffect(() => {
    if (visitorTypeCreate.status === Status.Ready) {
      onComplete(visitorTypeCreate.data);
    }
  }, [ visitorTypeCreate.status ]) // eslint-disable-line react-hooks/exhaustive-deps

  const [ bag, setFormBag ] = useState<FormBag<FormValues>>(createFormBag({ label: '', tag: '' }))

  const onUpdate = (event: FormUpdateEvent<FormValues>) => {
    setFormBag(event.bag)
  }

  return (
    <Modal
      onClose={props.onCancel}
      header={<h3>Create Visitor Type</h3>}
      footer={(
        <ButtonGroup>
          <Button onClick={props.onCancel} appearance="secondary">Cancel</Button>
          <Button onClick={createVisitorType} >
            Create Visitor Type
          </Button>
        </ButtonGroup>
      )}
    >
      <StatusGuard status={visitorTypeCreate} mode="form">
        <form noValidate onSubmit={event => {event.preventDefault()}}>
          <FormData bag={bag} onUpdate={onUpdate} validate={validate}>
            <Input
              label="Label"
              field="label"
              error={bag.touched.label ? bag.errors.label : undefined}
          />
            <Input
              label="Tag"
              field="tag"
              error={bag.touched.tag ? bag.errors.tag : undefined}
          />
          </FormData>
        </form>
      </StatusGuard>
    </Modal>
  );
};

export const VisitorTypeCreate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore>(
  store => ({
    visitorTypeCreate: store.getty.visitorTypeCreate
  }),
  {
    ...pick(actionCreators, 'gettyCreateVisitorTypeRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(VisitorTypeCreateView)
